import {
  readConsentSettings,
  toGoogleConsentUpdate,
  type ConsentSettings,
  type GoogleConsentUpdate,
} from "@/utils/consent";

declare global {
  interface Window {
    dataLayer?: unknown[];
    _gtmInitialized?: boolean;
    _gtmConsentDefaultSet?: boolean;
  }
}

const GTM_ID = "GTM-P5X2CZN3";

/** gtag-compatible push so Consent Mode commands are recognized by GTM/GA. */
function gtag(..._args: unknown[]): void {
  window.dataLayer = window.dataLayer || [];
  // Google's snippet relies on the Arguments object, not a plain array.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export function ensureConsentDefaults(): void {
  if (typeof window === "undefined") return;
  if (window._gtmConsentDefaultSet) return;

  window.dataLayer = window.dataLayer || [];
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  });
  // Enable cookieless / modeled measurement when storage is denied (Advanced Consent Mode).
  gtag("set", "ads_data_redaction", true);
  gtag("set", "url_passthrough", true);

  window._gtmConsentDefaultSet = true;
}

export function updateGoogleConsent(settings: ConsentSettings | null | undefined): void {
  if (typeof window === "undefined") return;
  ensureConsentDefaults();

  const update: GoogleConsentUpdate = toGoogleConsentUpdate(settings);
  gtag("consent", "update", update);
}

/**
 * Load GTM with Advanced Consent Mode.
 * Always loads (even when optional cookies are denied) so GA can still send
 * cookieless page pings without profiling cookies.
 */
export function initializeGtm(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  ensureConsentDefaults();

  // Sync Consent Mode with any already-saved choice before/while GTM boots.
  updateGoogleConsent(readConsentSettings());

  if (window._gtmInitialized) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

  const scriptId = "gtm-base";
  if (!document.getElementById(scriptId)) {
    const s = document.createElement("script");
    s.id = scriptId;
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
    document.head.appendChild(s);
  }

  window._gtmInitialized = true;
}

export function trackGtmPageView(pathname: string): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "page_view",
    page_path: pathname,
  });
}

export function pushDataLayerEvent(payload: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}
